import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'
import { authApi, AuthRefreshFailedError } from '@/lib/authApi'
import { reportApiError } from '@/lib/dev-error-capture'
import { cachedFetch } from '@/lib/api-cache'

/**
 * Cookie-based axios client (Phase 4 SEC-06 / D-05 / D-06).
 *
 * - withCredentials: true so leviathan_auth + leviathan_csrf + leviathan_refresh
 *   cookies fly with every request. The frontend NEVER reads the JWT itself.
 * - Request interceptor reads `leviathan_csrf` from document.cookie and echoes
 *   it as `X-CSRF-Token` (double-submit pattern enforced by api/core/csrf.py).
 * - Response interceptor handles 401 with a Set<RetriableConfig> tracker so
 *   N parallel 401s share ONE refresh attempt (FRAG-08 fix). On refresh
 *   failure every queued request rejects with the typed AuthRefreshFailedError
 *   and the logout + redirect happens exactly once.
 */

// Phase 8 SC#8: honour VITE_API_URL for production Cloudflare Pages deploys.
// In local dev VITE_API_URL is unset → baseURL is '/api' → Vite proxy handles it.
// In prod VITE_API_URL=https://api.leviathan.app → full URL bypasses the proxy.
const _apiOrigin: string = import.meta.env.VITE_API_URL ?? ''
const api: AxiosInstance = axios.create({
  baseURL: `${_apiOrigin}/api`,
  withCredentials: true,
})

// ---------------------------------------------------------------------------
// CSRF - read leviathan_csrf cookie and inject X-CSRF-Token header
// ---------------------------------------------------------------------------

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'))
  return match ? decodeURIComponent(match[1]) : null
}

api.interceptors.request.use((config) => {
  const csrf = readCookie('leviathan_csrf')
  if (csrf) {
    config.headers = config.headers ?? {}
    config.headers['X-CSRF-Token'] = csrf
  }
  return config
})

// ---------------------------------------------------------------------------
// FRAG-08 / D-06 - in-flight retry tracking via Set<RetriableConfig>
// ---------------------------------------------------------------------------

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean
}

const pendingRetries = new Set<RetriableConfig>()
let refreshPromise: Promise<void> | null = null
let logoutInFlight = false

async function performRefresh(): Promise<void> {
  // Single shared refresh promise - N parallel 401s all await this exact
  // instance. The promise is cleared in `.finally` AFTER all awaiters have
  // observed it, so the next 401 wave creates a fresh refresh.
  if (!refreshPromise) {
    refreshPromise = authApi
      .refresh()
      .then(() => {
        // Server rotated leviathan_auth + leviathan_refresh + leviathan_csrf
        // via Set-Cookie. Nothing for us to store.
      })
      .finally(() => {
        refreshPromise = null
      })
  }
  return refreshPromise
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    // Report all 4xx/5xx to dev error capture
    if (import.meta.env.DEV && error.response) {
      const status = error.response.status
      const endpoint = error.config?.url ?? 'unknown'
      const detail =
        (error.response.data as Record<string, unknown>)?.detail ?? error.message
      const message = typeof detail === 'string' ? detail : JSON.stringify(detail)
      reportApiError(endpoint, status, message)
    }
    const original = (error.config ?? {}) as RetriableConfig
    if (error.response?.status !== 401 || original._retry) {
      return Promise.reject(error)
    }
    original._retry = true
    pendingRetries.add(original)
    try {
      await performRefresh()
      // Refresh succeeded - re-issue this queued request. Cookies have been
      // rotated server-side, so the next call automatically picks them up.
      pendingRetries.delete(original)
      return await api(original)
    } catch {
      // Refresh failed - fail every queued request with AuthRefreshFailedError.
      const reason = new AuthRefreshFailedError(
        'Session refresh failed; please log in again.',
      )
      pendingRetries.clear()
      // Best-effort logout + single redirect (logoutInFlight guarantees once).
      if (!logoutInFlight) {
        logoutInFlight = true
        try {
          await authApi.logout()
        } catch {
          // Best-effort - server may already have cleared cookies.
        } finally {
          window.location.href = '/login'
          logoutInFlight = false
        }
      }
      return Promise.reject(reason)
    }
  },
)

export default api
export { api }

// ---------- Response types ----------

export interface TickerCard {
  ticker: string
  company_name: string | null
  rating: string | null
  model_summary: string | null
  price_target: number | null
  current_price: number | null
  implied_return: number | null
  sector: string | null
  analysis_date: string | null
  has_excel: boolean
  has_memo: boolean
  has_deck: boolean
  /** Client-only optimistic flag — set while a reanalyze job is in flight. Never returned by the server. */
  _reanalyzing?: boolean
}

export interface JobStatus {
  job_id: string
  ticker: string
  status: string
  phase?: string
  phase_message?: string
  progress: number
  cached: boolean
}

export interface JobDetail {
  job_id: string
  ticker: string
  status: string
  phase?: string
  phase_message?: string
  progress: number
  created_at: string
  started_at?: string
  completed_at?: string
  error?: string
  error_trace?: string
}

export interface HealthCheck {
  status: string
  uptime_seconds: number
  active_jobs: number
  queued_jobs: number
  completed_today: number
  workers?: { total: number; busy: number; available: number; stuck_jobs?: number }
  api_keys?: Record<string, boolean>
  cache_entries?: number
  database: string
}

export interface AnalyticsData {
  total_jobs: number
  completed_jobs: number
  failed_jobs: number
  user_errors: number
  leviathan_errors: number
  cancelled_jobs: number
  success_rate: number
  failure_rate: number
  avg_duration_seconds: number | null
  median_duration_seconds: number | null
  p95_duration_seconds: number | null
  jobs_by_day: Array<{ date: string; completed: number; user_errors: number; leviathan_errors: number }>
  recent_failures: Array<{ ticker: string; error: string; completed_at: string; error_type: 'user' | 'leviathan' }>
  top_errors: Array<{ error: string; count: number; error_type: 'user' | 'leviathan' }>
  rating_distribution: { BUY: number; HOLD: number; SELL: number }
  sector_distribution: Array<{ sector: string; count: number }>
  system: {
    uptime_seconds: number
    active_jobs: number
    queued_jobs: number
    completed_today: number
    workers: { total: number; busy: number; available: number; stuck_jobs?: number }
    cache_entries: number
    websocket_connections: number
  }
}

// ---------- API functions ----------

export interface TickersPage {
  tickers: TickerCard[]
  total: number
  limit: number
  offset: number
  has_next: boolean
}

export const fetchTickers = (limit = 20, offset = 0) =>
  api.get<TickersPage>('/tickers', { params: { limit, offset } }).then((r) => r.data)

// ---------- Analysis enrichment types ----------

export interface DCFScenario {
  price: number
  implied_return: number
  enterprise_value: number
  equity_value: number
  price_ggm?: number
  price_exit?: number
}

export interface DCFScenarios {
  bear?: DCFScenario
  base?: DCFScenario
  bull?: DCFScenario
}

export interface ConsensusYear {
  year: number
  our_revenue?: number
  street_revenue?: number | null
  revenue_delta_pct?: number | null
  our_eps?: number | null
  street_eps?: number | null
  eps_delta_pct?: number | null
  num_analysts?: number
}

export interface ConsensusComparison {
  years_comparison: ConsensusYear[]
  stance?: string
  model_vs_street_pt_pct?: number | null
}

export interface BandStats {
  count: number
  mean: number
  median: number
  std: number
  p25: number
  p75: number
  min: number
  max: number
}

export interface ValuationBand {
  series: Array<{ date: string; value: number }>
  stats: BandStats
  current?: number
}

export interface HistoricalValuationBands {
  available: boolean
  reason?: string
  pe?: ValuationBand
  ev_ebitda?: ValuationBand
  ps?: ValuationBand
  p_fcf?: ValuationBand
  data_years?: number[]
  price_datapoints?: number
}

export const fetchAnalysis = (ticker: string) =>
  api.get(`/analysis/${ticker}`).then((r) => r.data)

export const fetchSummary = (ticker: string) =>
  api.get(`/analysis/${ticker}/summary`).then((r) => r.data)

export const deleteTicker = (ticker: string) =>
  api.delete(`/analysis/${ticker}`).then((r) => r.data)

export const fetchFilings = (ticker: string) =>
  api
    .get<{
      sections: {
        business_description: string | null
        risk_factors: string | null
        mda: string | null
      }
      press_releases: Array<{ date: string; content: string }>
      earnings_transcript: Record<string, unknown> | null
    }>(`/analysis/${ticker}/filings`)
    .then((r) => r.data)

export type IntrinsicSeriesPoint = {
  date: string
  dcf: number | null
  comps_ebitda: number | null
  comps_pe: number | null
}

export type IntrinsicSeriesMeta = {
  coverage_start: string | null
  peers_used: string[]
  degraded: boolean
  degraded_reasons: string[]
}

export const fetchPriceChart = (ticker: string) =>
  api
    .get<{
      prices: Array<{ date: string; close: number }>
      dcf_price: number | null
      price_target: number | null
      current_price: number | null
      intrinsic_label?: string
      // Historical intrinsic-value extension (spec §7). Optional: when
      // the HISTORICAL_INTRINSIC_V1 flag is off server-side these
      // fields are absent and the legacy flat-line render stays in use.
      intrinsic_series?: IntrinsicSeriesPoint[]
      series_meta?: IntrinsicSeriesMeta
      comps_ebitda_price?: number | null
      comps_pe_price?: number | null
    }>(`/analysis/${ticker}/price-chart`)
    .then((r) => r.data)

export const fetchCompare = (tickers: string[], sections?: string[]) =>
  api
    .get('/compare', {
      params: {
        tickers: tickers.join(','),
        ...(sections ? { sections: sections.join(',') } : {}),
      },
    })
    .then((r) => r.data)

export const createJob = (ticker: string, force = false) =>
  api.post<JobStatus>('/jobs', { ticker, force }).then((r) => r.data)

export const fetchJob = (jobId: string) =>
  api.get<JobDetail>(`/jobs/${jobId}`).then((r) => r.data)

export const fetchJobs = (params?: {
  status?: string
  limit?: number
  offset?: number
}) =>
  api
    .get<{ jobs: JobDetail[]; total: number }>('/jobs', { params })
    .then((r) => r.data)

export const cancelJob = (jobId: string) =>
  api.delete(`/jobs/${jobId}`).then((r) => r.data)

export const retryFailedJobs = () =>
  api.post<{ retried: number; jobs: JobStatus[] }>('/jobs/retry-failed').then((r) => r.data)

export const pauseQueue = () =>
  api.post<{ paused: boolean }>('/devtools/queue/pause').then((r) => r.data)

export const resumeQueue = () =>
  api.post<{ paused: boolean }>('/devtools/queue/resume').then((r) => r.data)

export const getQueueStatus = () =>
  api.get<{ paused: boolean }>('/devtools/queue/status').then((r) => r.data)

export const clearQueue = () =>
  api.post<{ cancelled: number }>('/devtools/queue/clear').then((r) => r.data)

export const createScreen = (tickers: string[], force = false) =>
  api.post('/screen', { tickers, force }).then((r) => r.data)

export const fetchHealth = () =>
  api.get<HealthCheck>('/health').then((r) => r.data)

export const fetchAnalytics = () =>
  api.get<AnalyticsData>('/analytics').then((r) => r.data)

export const getDownloadUrl = (ticker: string, type: string) =>
  `/api/download/${ticker}/${type}`

/** Authenticated file download - fetches via axios (with JWT) and triggers browser save. */
export async function downloadFile(ticker: string, type: string): Promise<void> {
  const resp = await api.get(`/download/${ticker}/${type}`, { responseType: 'blob' })
  const disposition = resp.headers['content-disposition'] ?? ''
  const match = disposition.match(/filename="?(.+?)"?$/i)
  const filename = match?.[1] ?? `${ticker}_${type}`
  const url = URL.createObjectURL(resp.data as Blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url) }, 200)
}

// ---------- Manual Input ----------

export interface ManualInputPayload {
  company_name: string
  sector: string
  shares_outstanding: number
  estimated_equity_value?: number
  current_price?: number
  currency?: string
  fiscal_year_end?: string
  business_description?: string
  income_statement: Array<{
    year: number
    revenue?: number
    cost_of_revenue?: number
    gross_profit?: number
    operating_income?: number
    net_income?: number
    da?: number
    interest_expense?: number
    income_tax?: number
    eps_diluted?: number
    diluted_shares?: number
  }>
  balance_sheet?: Array<{
    year: number
    total_assets?: number
    cash?: number
    accounts_receivable?: number
    inventory?: number
    total_debt?: number
    shareholders_equity?: number
    shares_outstanding?: number
  }>
  cash_flow?: Array<{
    year: number
    operating_cash_flow?: number
    capex?: number
    dividends?: number
  }>
  private_adjustments?: {
    illiquidity_discount?: number
    control_premium?: number
    minority_discount?: number
    key_person_discount?: number
    excess_compensation?: number
  }
  comparable_transactions?: Array<{
    target_name: string
    date?: string
    enterprise_value?: number
    target_revenue?: number
    target_ebitda?: number
    ev_revenue?: number
    ev_ebitda?: number
    premium_pct?: number
  }>
  force?: boolean
}

export interface ManualInputResult {
  job_id: string
  identifier: string
  status: string
  data_confidence?: { score: number; tier: string }
}

export const submitManualInput = (data: ManualInputPayload) =>
  api.post<ManualInputResult>('/manual-input', data).then((r) => r.data)

export const fetchSectors = () =>
  api
    .get<{
      sectors: Array<{
        key: string
        label: string
        primary_framework: string
        valuation_method: string
      }>
    }>('/manual-input/sectors')
    .then((r) => r.data)

export const fetchTemplate = () =>
  api.get('/manual-input/template').then((r) => r.data)

// ---------- M&A Deal Analyzer ----------

import type {
  MNAJobCreate,
  MNAJobStatus,
  MNAAnalysis,
  MNASummary,
  QuickLBOInput,
  QuickAccretionInput,
  QuickImpliedInput,
  MNADealComparisonRequest,
  MNADealComparison,
  MNAFinancingOptimizerRequest,
  MNAFinancingResult,
  MNAScenarioEngineRequest,
  MNAScenarioResult,
  MNARegulatoryRiskRequest,
  MNARegulatoryRiskResult,
  MNAExecutiveSummaryRequest,
  MNAExecutiveSummary,
} from './mna-types'

export const createMNAJob = (data: MNAJobCreate) =>
  api.post<MNAJobStatus>('/mna/jobs', data).then((r) => r.data)

export const fetchMNAJobs = (params?: { status?: string; limit?: number; offset?: number }) =>
  api
    .get<{ jobs: MNAJobStatus[]; total: number }>('/mna/jobs', { params })
    .then((r) => r.data)

export const fetchMNAJob = (jobId: string) =>
  api.get<MNAJobStatus>(`/mna/jobs/${jobId}`).then((r) => r.data)

export const cancelMNAJob = (jobId: string) =>
  api.delete(`/mna/jobs/${jobId}`).then((r) => r.data)

export const deleteMNAJob = (jobId: string) =>
  api.delete(`/mna/jobs/${jobId}`).then((r) => r.data)

export const fetchMNAAnalysis = (acquirer: string, target: string) =>
  api.get<MNAAnalysis>(`/mna/analysis/${acquirer}/${target}`).then((r) => r.data)

export const fetchMNASummary = (acquirer: string, target: string) =>
  api.get<MNASummary>(`/mna/analysis/${acquirer}/${target}/summary`).then((r) => r.data)

export const quickPaperLBO = (data: QuickLBOInput) =>
  api.post('/mna/quick/paper-lbo', data).then((r) => r.data)

export const quickAccretion = (data: QuickAccretionInput) =>
  api.post('/mna/quick/accretion', data).then((r) => r.data)

export const quickImpliedValuation = (data: QuickImpliedInput) =>
  api.post('/mna/quick/implied-valuation', data).then((r) => r.data)

export const getMNADownloadUrl = (acquirer: string, target: string, type: string) =>
  `/api/mna/download/${acquirer}/${target}/${type}`

// ---------- M&A New Components ----------

// Component 1: Deal Comparison Engine
export const compareMNATargets = (data: MNADealComparisonRequest) =>
  api.post<MNADealComparison>('/mna/compare', data).then((r) => r.data)

// Component 2: Financing Structure Optimizer
export const optimizeFinancing = (data: MNAFinancingOptimizerRequest) =>
  api.post<MNAFinancingResult>('/mna/financing-optimizer', data).then((r) => r.data)

// Component 3: Monte Carlo Scenario Engine
export const runScenarioAnalysis = (data: MNAScenarioEngineRequest) =>
  api.post<MNAScenarioResult>('/mna/scenario-analysis', data).then((r) => r.data)

// Component 4: Enhanced Regulatory Risk Scoring
export const assessRegulatoryRisk = (data: MNARegulatoryRiskRequest) =>
  api.post<MNARegulatoryRiskResult>('/mna/regulatory-risk', data).then((r) => r.data)

// Component 5: Executive Summary Generator
export const generateExecutiveSummary = (data: MNAExecutiveSummaryRequest) =>
  api.post<MNAExecutiveSummary>('/mna/executive-summary', data).then((r) => r.data)

export const fetchExecutiveSummary = (acquirer: string, target: string) =>
  api.get<MNAExecutiveSummary>(`/mna/executive-summary/${acquirer}/${target}`).then((r) => r.data)

// ---------- Display Config ----------

import type { ResolvedConfig, TemplateInfo } from '@/types/display-config'

export const fetchDisplayConfig = (): Promise<ResolvedConfig> =>
  api.get<ResolvedConfig>('/display-config').then((r) => r.data)

export const updateAnalystOverrides = (overrides: Record<string, string>): Promise<void> =>
  api.put('/display-config/analyst', { overrides }).then(() => undefined)

export const resetAnalystOverrides = (): Promise<void> =>
  api.delete('/display-config/analyst').then(() => undefined)

export const updateFirmConfig = (body: {
  template?: string
  overrides?: Record<string, string>
}): Promise<void> =>
  api.put('/display-config/firm', body).then(() => undefined)

export const applyFirmTemplate = (template: string): Promise<void> =>
  api.post('/display-config/firm/template', { template }).then(() => undefined)

export const fetchTemplates = (): Promise<TemplateInfo[]> =>
  api.get<{ templates: TemplateInfo[] }>('/display-config/templates').then((r) => r.data.templates)

// ── Watchlist & Coverage ────────────────────────────────
export interface WatchlistItem {
  id: number
  ticker: string
  added_at: string
  last_run_at: string | null
  run_frequency: string
  enabled: boolean
  notes: string | null
  latest_rating: string | null
  latest_price_target: number | null
  latest_implied_return: number | null
}

export interface AlertConfig {
  id: number
  ticker: string
  alert_type: string
  threshold_value: number | null
  direction: string
  enabled: boolean
  created_at: string
  last_triggered_at: string | null
}

export interface CoverageRun {
  id: number
  ticker: string
  run_at: string
  status: string
  rating: string | null
  price_target: number | null
  implied_return: number | null
  prev_rating: string | null
  prev_price_target: number | null
  delta_price_target: number | null
  alerts_triggered: number[] | null
  error: string | null
}

// Watchlist API functions
export const getWatchlist = () =>
  api.get<WatchlistItem[]>('/watchlist/').then(r => r.data)

export const addToWatchlist = (ticker: string, frequency = 'weekly', notes?: string) =>
  api.post<WatchlistItem>('/watchlist/', { ticker, run_frequency: frequency, notes }).then(r => r.data)

export const removeFromWatchlist = (ticker: string) =>
  api.delete(`/watchlist/${ticker}`)

export const updateWatchlistItem = (ticker: string, updates: Partial<WatchlistItem>) =>
  api.patch<WatchlistItem>(`/watchlist/${ticker}`, updates).then(r => r.data)

export const getWatchlistAlerts = (ticker: string) =>
  api.get<AlertConfig[]>(`/watchlist/${ticker}/alerts`).then(r => r.data)

export const createAlert = (ticker: string, alertType: string, threshold?: number, direction = 'both') =>
  api.post<AlertConfig>(`/watchlist/${ticker}/alerts`, {
    alert_type: alertType, threshold_value: threshold, direction
  }).then(r => r.data)

export const deleteAlert = (alertId: number) =>
  api.delete(`/watchlist/alerts/${alertId}`)

export const getCoverageHistory = (ticker: string) =>
  api.get<CoverageRun[]>(`/watchlist/${ticker}/history`).then(r => r.data)

export const triggerCoverageRun = (ticker: string) =>
  api.post<{ run_id: number }>(`/watchlist/${ticker}/run`).then(r => r.data)

export const getTriggeredAlerts = () =>
  api.get<CoverageRun[]>('/watchlist/alerts/triggered').then(r => r.data)

// ── Events ───────────────────────────────────────────────

export interface EventItem {
  id: number
  ticker: string
  event_type: string
  event_subtype?: string
  severity: string
  title: string
  detail?: string | null
  source?: string | null
  filed_at?: string | null
  detected_at: string
  triggered_reanalysis: boolean
  job_id?: string | null
  acknowledged: boolean
}

export interface EventSubscription {
  id: number
  ticker: string
  event_type: string
  enabled: boolean
  notify_email: boolean
  notify_websocket: boolean
  created_at: string
  updated_at: string
}

export interface EventStats {
  total: number
  unacknowledged: number
  by_severity: Record<string, number>
  by_type: Record<string, number>
  reanalyses_triggered: number
}

export const fetchEvents = (ticker: string) =>
  api.get<EventItem[]>(`/events/${ticker}`).then(r => r.data)

export const fetchAllEvents = (limit?: number, offset?: number) =>
  api.get<{ events: EventItem[]; total: number }>('/events', { params: { limit, offset } }).then(r => r.data)

export const fetchEventStats = () =>
  api.get<EventStats>('/events/stats').then(r => r.data)

export const createEventSubscription = (data: Omit<EventSubscription, 'id' | 'created_at' | 'updated_at'>) =>
  api.post<EventSubscription>('/events/subscriptions', data).then(r => r.data)

export const updateEventSubscription = (id: number, data: Partial<EventSubscription>) =>
  api.patch<EventSubscription>(`/events/subscriptions/${id}`, data).then(r => r.data)

export const deleteEventSubscription = (id: number) =>
  api.delete(`/events/subscriptions/${id}`).then(r => r.data)

export const fetchEventSubscriptions = (ticker: string) =>
  api.get<EventSubscription[]>(`/events/${ticker}/subscriptions`).then(r => r.data)

export const acknowledgeEvent = (id: number) =>
  api.post(`/events/${id}/acknowledge`).then(r => r.data)

export const acknowledgeAllEvents = () =>
  api.post<{ acknowledged_count: number }>('/events/acknowledge-all').then(r => r.data)

// ── Portfolio ────────────────────────────────────────────

export interface PortfolioHolding {
  ticker: string
  weight: number
  shares: number | null
  cost_basis: number | null
  current_price: number | null
  market_value: number | null
  gain_loss_pct: number | null
  rating: string | null
  implied_return: number | null
}

export interface Portfolio {
  id: number
  name: string
  description: string | null
  holdings: PortfolioHolding[]
  created_at: string
  updated_at: string
  total_value: number | null
}

export interface PortfolioAnalytics {
  tickers: string[]
  weights: number[]
  lookback_days?: number
  portfolio_id?: string
  portfolio_name?: string
  risk_metrics: Record<string, number>
  var: {
    parametric_var: number
    historical_var: number
    monte_carlo_var: number
    conditional_var: number
    [key: string]: unknown
  }
  correlation: { tickers: string[]; matrix: number[][] }
  factor_exposure: {
    portfolio_beta: number
    sector_breakdown: Record<string, number>
    sector_hhi: number
    top1_concentration: number
    top3_concentration: number
    ticker_count: number
    holdings_detail: Array<Record<string, unknown>>
  }
}

export interface StressTestResult {
  portfolio_id: number
  scenarios: Array<{
    name: string
    description: string
    portfolio_impact_pct: number
    holdings_impact: Array<{
      ticker: string
      impact_pct: number
    }>
  }>
}

export const fetchPortfolios = () =>
  api.get<Portfolio[]>('/portfolio').then(r => r.data)

export const createPortfolio = (data: { name: string; description?: string; holdings: Array<{ ticker: string; weight: number; shares?: number; cost_basis?: number }> }) =>
  api.post<Portfolio>('/portfolio', data).then(r => r.data)

export const fetchPortfolio = (id: number) =>
  api.get<Portfolio>(`/portfolio/${id}`).then(r => r.data)

export const updatePortfolio = (id: number, data: Partial<Portfolio>) =>
  api.patch<Portfolio>(`/portfolio/${id}`, data).then(r => r.data)

export const deletePortfolio = (id: number) =>
  api.delete(`/portfolio/${id}`).then(r => r.data)

export const fetchPortfolioAnalytics = (id: number) =>
  api.get<PortfolioAnalytics>(`/portfolio/${id}/analytics`).then(r => r.data)

export const runStressTest = (id: number) =>
  api.post<StressTestResult>(`/portfolio/${id}/stress-test`).then(r => r.data)

export const runAdHocAnalytics = (tickers: string[], weights: number[]) =>
  api.post<PortfolioAnalytics>('/portfolio/ad-hoc', { tickers, weights }).then(r => r.data)

export const fetchBenchmarkComparison = (id: string, benchmark = 'SPY') =>
  api.get(`/portfolio/${id}/benchmark`, { params: { benchmark } }).then(r => r.data)

export const fetchPerformanceHistory = (id: string, lookbackDays?: number) =>
  api.get(`/portfolio/${id}/performance`, { params: lookbackDays != null ? { lookback_days: lookbackDays } : {} }).then(r => r.data)

export const fetchConstrainedFrontier = (id: string, maxWeight?: number, minWeight?: number) =>
  api.get(`/portfolio-risk/${id}/constrained-frontier`, {
    params: {
      ...(maxWeight != null ? { max_weight: maxWeight } : {}),
      ...(minWeight != null ? { min_weight: minWeight } : {}),
    },
  }).then(r => r.data)

// ── Coverage History ─────────────────────────────────────

export interface AnalysisSnapshot {
  id: number
  ticker: string
  snapshot_date: string
  rating: string
  price_target: number
  implied_return: number
  current_price: number
  wacc: number | null
  terminal_growth: number | null
  revenue_growth: number | null
  ebitda_margin: number | null
  analysis_hash: string | null
}

export interface AttributionResult {
  ticker: string
  from_snapshot: AnalysisSnapshot
  to_snapshot: AnalysisSnapshot
  price_target_change: number
  drivers: Array<{
    factor: string
    contribution_pct: number
    old_value: number | null
    new_value: number | null
  }>
}

export interface StalenessResult {
  ticker: string
  last_analysis_date: string
  days_since_analysis: number
  staleness_score: number
  stale: boolean
  events_since: number
  price_change_pct: number
  recommendation: string
}

export interface AccuracyResult {
  ticker: string
  rating: string
  price_target: number
  analysis_date: string
  current_price: number
  actual_return_pct: number
  target_hit: boolean
  direction_correct: boolean
  days_elapsed: number
}

export const fetchSnapshots = (ticker: string) =>
  api.get<AnalysisSnapshot[]>(`/coverage/${ticker}/snapshots`).then(r => r.data)

export const fetchAttribution = (ticker: string, fromId: number, toId: number) =>
  api.get<AttributionResult>(`/coverage/${ticker}/attribution`, { params: { from_id: fromId, to_id: toId } }).then(r => r.data)

export const fetchStaleness = (ticker: string) =>
  api.get<StalenessResult>(`/coverage/${ticker}/staleness`).then(r => r.data)

export const fetchAccuracy = () =>
  api.get<AccuracyResult[]>('/coverage/accuracy').then(r => r.data)

export const fetchAuditLog = (ticker: string) =>
  api.get<AnalysisSnapshot[]>(`/coverage/${ticker}/audit-log`).then(r => r.data)

// ── Compliance ───────────────────────────────────────────

export interface ComplianceSettings {
  firm_name: string
  analyst_name: string
  analyst_title?: string
  analyst_certification_text?: string
  research_classification: string
  conflicts_statement?: string
  general_disclaimer?: string
  mifid_enabled: boolean
  finra_enabled: boolean
  recommendation_validity_days: number
}

export interface RatingDistribution {
  counts: Record<string, number>
  percentages: Record<string, number>
  total_covered: number
  as_of: string
  disclosure: string
}

export interface RecommendationHistoryEntry {
  id: number | null
  ticker: string
  rating: string
  price_target: number | null
  current_price: number | null
  implied_return: number | null
  analysis_date: string
  created_at: string | null
}

export const fetchComplianceSettings = () =>
  api.get<ComplianceSettings>('/compliance/settings').then(r => r.data)

export const updateComplianceSettings = (data: Partial<ComplianceSettings>) =>
  api.patch<ComplianceSettings>('/compliance/settings', data).then(r => r.data)

export const fetchRatingDistribution = () =>
  api.get<RatingDistribution>('/compliance/rating-distribution').then(r => r.data)

export const fetchRecommendationHistory = (ticker?: string) =>
  api.get<RecommendationHistoryEntry[]>('/compliance/recommendation-history', { params: { ticker } }).then(r => r.data)

export const fetchAllRecommendationHistory = (page?: number, pageSize?: number) =>
  api.get<RecommendationHistoryEntry[]>('/compliance/recommendation-history', {
    params: { ...(page != null ? { page } : {}), ...(pageSize != null ? { page_size: pageSize } : {}) },
  }).then(r => r.data)

// ── Estimates ────────────────────────────────────────────

export interface EstimateSnapshot {
  id: number
  ticker: string
  metric: string
  period: string
  estimate_value: number
  actual_value: number | null
  consensus_value: number | null
  snapshot_date: string
  source: string
}

export interface EarlyCallEvent {
  id: number
  ticker: string
  metric: string
  period: string
  our_estimate: number
  consensus_at_call: number
  actual_value: number | null
  consensus_final: number | null
  call_date: string
  direction: string
  magnitude_pct: number
  validated: boolean | null
}

export interface AccuracyEntry {
  ticker: string
  metric: string
  period: string
  our_estimate: number
  actual_value: number
  error_pct: number
  consensus_estimate: number | null
  consensus_error_pct: number | null
  beat_consensus: boolean
}

export interface CrossCoverageSummary {
  total_revisions: number
  up: number
  down: number
  flat: number
  up_pct: number
  down_pct: number
  by_sector: Record<string, unknown>
  most_revised: Array<{
    ticker: string
    revision_count: number
    net_direction: string
    latest_eps_delta_pct: number | null
  }>
  ticker_count: number
}

export const fetchEstimateHistory = (ticker: string) =>
  api.get<EstimateSnapshot[]>(`/estimates/${ticker}/history`).then(r => r.data)

export const fetchRevisions = (ticker: string) =>
  api.get<EstimateSnapshot[]>(`/estimates/${ticker}/revisions`).then(r => r.data)

export const fetchEarlyCalls = (ticker: string) =>
  api.get<EarlyCallEvent[]>(`/estimates/${ticker}/early-calls`).then(r => r.data)

export const fetchEstimateAccuracy = (ticker: string) =>
  api.get<AccuracyEntry[]>(`/estimates/${ticker}/accuracy`).then(r => r.data)

export const fetchCrossCoverage = (months?: number) =>
  api.get<CrossCoverageSummary>('/estimates/cross-coverage', { params: months != null ? { months } : {} }).then(r => r.data)

export const submitActuals = (data: { ticker: string; metric: string; period: string; actual_value: number }) =>
  api.post('/estimates/actuals', data).then(r => r.data)

// ── Peer Groups ──────────────────────────────────────────

export interface PeerGroupMember {
  ticker: string
  company_name: string | null
  market_cap: number | null
  sector: string | null
  added_at: string
  is_primary: boolean
}

export interface PeerGroup {
  id: string
  name: string
  description: string | null
  created_at: string
  updated_at: string
  member_count: number
  is_default: boolean
}

export interface PeerGroupDetail extends PeerGroup {
  members: PeerGroupMember[]
}

export const fetchPeerGroups = () =>
  api.get<PeerGroup[]>('/peer-groups').then(r => r.data)

export const createPeerGroup = (data: { name: string; description?: string; tickers?: string[] }) =>
  api.post<PeerGroupDetail>('/peer-groups', data).then(r => r.data)

export const fetchPeerGroup = (id: string) =>
  api.get<PeerGroupDetail>(`/peer-groups/${id}`).then(r => r.data)

export const updatePeerGroup = (id: string, data: Partial<PeerGroup>) =>
  api.patch<PeerGroupDetail>(`/peer-groups/${id}`, data).then(r => r.data)

export const deletePeerGroup = (id: string) =>
  api.delete(`/peer-groups/${id}`).then(r => r.data)

export const addPeerGroupMember = (id: string, ticker: string) =>
  api.post<PeerGroupMember>(`/peer-groups/${id}/members`, { ticker }).then(r => r.data)

export const removePeerGroupMember = (id: string, ticker: string) =>
  api.delete(`/peer-groups/${id}/members/${ticker}`).then(r => r.data)

export const setPeerGroupDefault = (id: string) =>
  api.post(`/peer-groups/${id}/set-default`).then(r => r.data)

export const searchPeers = (query: string) =>
  api.get<PeerGroupMember[]>('/peer-groups/search', { params: { q: query } }).then(r => r.data)

export const fetchPeerGroupTemplates = () =>
  api.get<Array<{ name: string; description: string; tickers: string[] }>>('/peer-groups/templates').then(r => r.data)

// ── PDF Import ───────────────────────────────────────────

export interface PDFExtractionResponse {
  extraction_id: string
  filename: string
  total_pages: number
  extraction_method: string
  metadata: {
    company_name?: string
    currency?: string
    scale_factor: number
    fiscal_year_end?: string
  }
  income_statement: Array<Record<string, number | string>>
  balance_sheet: Array<Record<string, number | string>>
  cash_flow: Array<Record<string, number | string>>
  unmatched_rows: Array<{ label: string; values: Record<string, string> }>
  extraction_confidence: number
  warnings: string[]
}

export const uploadPDF = (file: File) => {
  const formData = new FormData()
  formData.append('file', file)
  return api.post<PDFExtractionResponse>('/pdf-import/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data)
}

// ── Dev Tools ─────────────────────────────────────────────

export interface DataSourceInfo {
  name: string
  enabled: boolean
  requires_key: boolean
  has_key: boolean
  api_key_env: string | null
  rate_limit: string
  rate_limit_raw: { calls: number; period_seconds: number }
  priority: number
  categories: string[]
}

export const fetchDataSources = () =>
  api.get<{ sources: DataSourceInfo[] }>('/devtools/sources').then(r => r.data)

export const toggleDataSource = (name: string, enabled: boolean) =>
  api.patch<{ name: string; enabled: boolean }>(`/devtools/sources/${name}`, { enabled }).then(r => r.data)

// ── AI Usage & Cost (Phase 0.5 Task 6) ───────────────────────────────────────

export interface AILocalUsageByType {
  narrative_type: string
  calls: number
  successful_calls: number
  failed_calls: number
  input_tokens: number
  output_tokens: number
  cache_creation_input_tokens: number
  cache_read_input_tokens: number
  estimated_cost_usd: number
}

export interface AILocalUsageByDate {
  date: string
  calls: number
  successful_calls: number
  failed_calls: number
  input_tokens: number
  output_tokens: number
  estimated_cost_usd: number
}

export interface AILocalUsage {
  window_days: number
  since: string
  total_calls: number
  successful_calls: number
  failed_calls: number
  success_rate: number
  input_tokens: number
  output_tokens: number
  cache_creation_input_tokens: number
  cache_read_input_tokens: number
  avg_latency_ms: number
  estimated_cost_usd: number
  by_narrative_type: AILocalUsageByType[]
  by_date: AILocalUsageByDate[]
}

export interface AIAdminUsageModelEntry {
  input_tokens: number
  output_tokens: number
  cache_creation_input_tokens: number
  cache_read_input_tokens: number
  estimated_cost_usd: number
}

export interface AIAdminUsageSnapshot {
  available: true
  id: string
  snapshot_date: string
  input_tokens: number
  output_tokens: number
  cache_creation_input_tokens: number
  cache_read_input_tokens: number
  total_cost_usd: number
  model_breakdown: Record<string, AIAdminUsageModelEntry>
  fetched_at: string
  source: 'cache' | 'fresh' | 'cache_stale'
}

export interface AIAdminUsageUnavailable {
  available: false
  snapshot_date: string
  reason: string
  message: string
}

export type AIAdminUsageResponse = AIAdminUsageSnapshot | AIAdminUsageUnavailable

export interface AIAdminRefreshResult {
  start_date: string
  end_date: string
  refreshed: Array<{
    date: string
    total_cost_usd: number
    input_tokens: number
    output_tokens: number
    source: string | null
  }>
  failed_dates: string[]
  refreshed_count: number
  failed_count: number
}

export const getAILocalUsage = (days: number) =>
  api.get<AILocalUsage>('/devtools/ai-usage/local', { params: { days } }).then(r => r.data)

export const getAIAdminUsage = (date: string) =>
  api
    .get<AIAdminUsageResponse>('/devtools/ai-usage/admin', { params: { date } })
    .then(r => r.data)

export const refreshAIAdminUsage = (startDate: string, endDate: string) =>
  api
    .post<AIAdminRefreshResult>('/devtools/ai-usage/admin/refresh', {
      start_date: startDate,
      end_date: endDate,
    })
    .then(r => r.data)

import type { FullNarrativeResponse, InvestmentThesisResponse, RiskNarrativeResponse, TranscriptAnalysisResponse } from '@/types/ai'
import type { BacktestRecord, BacktestStats, BacktestEvaluateResult, BacktestLeaderboard } from '@/types/backtesting'
import type { ScreenQueryResponse, QueryFieldsResponse, SavedScreen } from '@/types/screening'
import type { Webhook, WebhookCreateRequest, WebhookUpdateRequest, WebhookTestResult } from '@/types/webhooks'
import type { Annotation, AnnotationCreateRequest } from '@/types/annotations'
import type { BatchJob, BatchListResponse, BatchResultsResponse } from '@/types/batch'
import type { FactorUniverseResponse, TickerFactorResponse, FactorScreenResponse } from '@/types/factors'
import type { MLTrainResult, MLSeedResult, MLPrediction, MLModelStats, MLFactor, MLBatchPrediction } from '@/types/ml'
import type { UsageSummary, UsageHistoryResponse, UsageQuota } from '@/types/usage'
import type {
  PortfolioExposure, PortfolioVaR, PortfolioAttribution, PortfolioFrontier,
  AdHocVaRResult, AdHocFrontierResult, DrawdownAnalysis, RollingMetrics,
  ConcentrationRisk, WhatIfResult, RiskParityResult,
  StrategyTypeSchema, StrategyRun,
} from '@/types/portfolio_advanced'
import type { MoatAnalysisResponse, MoatSummaryResponse } from '@/types/moat'

// ── AI Narrative Engine ──────────────────────────────────────────────────────
export const fetchAINarrative = (ticker: string): Promise<FullNarrativeResponse> =>
  api.get(`/ai/${ticker}/narrative`).then(r => r.data)

export const generateAINarrative = (ticker: string): Promise<FullNarrativeResponse> =>
  api.post(`/ai/${ticker}/narrative`).then(r => r.data)

export const fetchAIThesis = (ticker: string): Promise<InvestmentThesisResponse> =>
  api.get(`/ai/${ticker}/thesis`).then(r => r.data)

export const fetchAIRisks = (ticker: string): Promise<RiskNarrativeResponse> =>
  api.get(`/ai/${ticker}/risks`).then(r => r.data)

export const analyzeTranscript = (ticker: string, text: string): Promise<TranscriptAnalysisResponse> =>
  api.post(`/ai/${ticker}/transcript`, { text }).then(r => r.data)

// ── Backtesting ──────────────────────────────────────────────────────────────
// Cached via lib/api-cache so sidebar hover prefetch and page-mount fetch
// share a single in-flight request and a 60s TTL.
// NOTE: Pages define their own local BacktestRecord -- return unknown to avoid type clash
export const fetchBacktestRecords = (params?: { ticker?: string; limit?: number; offset?: number }): Promise<unknown> => {
  const key = `GET /backtesting/records?${new URLSearchParams(
    Object.entries(params ?? {}).filter(([, v]) => v != null).map(([k, v]) => [k, String(v)]),
  ).toString()}`
  return cachedFetch(key, () => api.get('/backtesting/records', { params }).then(r => r.data))
}
export const cacheKeyBacktestRecords = (params?: { ticker?: string; limit?: number; offset?: number }): string =>
  `GET /backtesting/records?${new URLSearchParams(
    Object.entries(params ?? {}).filter(([, v]) => v != null).map(([k, v]) => [k, String(v)]),
  ).toString()}`

// NOTE: Pages define their own local BacktestStats -- return unknown to avoid type clash
export const CACHE_KEY_BACKTEST_STATS = 'GET /backtesting/stats'
export const fetchBacktestStats = (ticker?: string): Promise<unknown> => {
  const key = ticker ? `GET /backtesting/stats/${ticker}` : CACHE_KEY_BACKTEST_STATS
  return cachedFetch(key, () =>
    ticker
      ? api.get(`/backtesting/stats/${ticker}`).then(r => r.data)
      : api.get('/backtesting/stats').then(r => r.data),
  )
}

export const evaluateBacktests = (): Promise<BacktestEvaluateResult> =>
  api.post('/backtesting/evaluate').then(r => r.data)

// NOTE: Pages define their own local leaderboard type -- return unknown to avoid type clash
export const CACHE_KEY_BACKTEST_LEADERBOARD = 'GET /backtesting/leaderboard'
export const fetchBacktestLeaderboard = (): Promise<unknown> =>
  cachedFetch(CACHE_KEY_BACKTEST_LEADERBOARD, () => api.get('/backtesting/leaderboard').then(r => r.data))

// ── Screening Query ──────────────────────────────────────────────────────────
export const runScreenQuery = (expression: string, limit = 50): Promise<ScreenQueryResponse> =>
  api.post('/screen/query', { expression, limit }).then(r => r.data)

export const fetchQueryFields = (): Promise<QueryFieldsResponse> =>
  api.get('/screen/fields').then(r => r.data)

// NOTE: API may return SavedScreen[] or { screens: SavedScreen[] } -- callers handle both
export const fetchSavedScreens = (): Promise<SavedScreen[] | { screens: SavedScreen[] }> =>
  api.get('/screen/saved').then(r => r.data)

export const saveScreen = (name: string, expression: string, description?: string): Promise<SavedScreen> =>
  api.post('/screen/saved', { name, expression, description }).then(r => r.data)

export const runSavedScreen = (id: string): Promise<ScreenQueryResponse> =>
  api.post(`/screen/saved/${id}/run`).then(r => r.data)

export const deleteSavedScreen = (id: string): Promise<void> =>
  api.delete(`/screen/saved/${id}`)

// ── Webhooks ─────────────────────────────────────────────────────────────────
export const fetchWebhooks = (): Promise<Webhook[]> =>
  api.get('/webhooks').then(r => r.data)

export const createWebhook = (data: WebhookCreateRequest): Promise<Webhook> =>
  api.post('/webhooks', data).then(r => r.data)

export const updateWebhook = (id: string, data: WebhookUpdateRequest): Promise<Webhook> =>
  api.patch(`/webhooks/${id}`, data).then(r => r.data)

export const deleteWebhook = (id: string): Promise<void> =>
  api.delete(`/webhooks/${id}`)

export const testWebhook = (id: string): Promise<WebhookTestResult> =>
  api.post(`/webhooks/${id}/test`).then(r => r.data)

// ── Annotations ──────────────────────────────────────────────────────────────
// NOTE: callers define their own local Annotation interface -- return unknown to avoid clash
export const fetchAnnotations = (ticker: string): Promise<unknown> =>
  api.get(`/annotations/${ticker}`).then(r => r.data)

export const createAnnotation = (ticker: string, data: AnnotationCreateRequest): Promise<unknown> =>
  api.post(`/annotations/${ticker}`, data).then(r => r.data)

export const deleteAnnotation = (ticker: string, id: string): Promise<void> =>
  api.delete(`/annotations/${ticker}/${id}`)

// ── Credit Analysis ───────────────────────────────────────────────────────────
export const fetchCreditAnalysis = (ticker: string): Promise<unknown> =>
  api.get(`/credit/${ticker}`).then(r => r.data)

export const fetchCreditScore = (ticker: string): Promise<unknown> =>
  api.get(`/credit/${ticker}/score`).then(r => r.data)

// ── Institutional Ownership ───────────────────────────────────────────────────
export const fetchInstitutionalHolders = (ticker: string): Promise<unknown> =>
  api.get(`/institutional/${ticker}/holders`).then(r => r.data)

export const fetchOwnershipMetrics = (ticker: string): Promise<unknown> =>
  api.get(`/institutional/${ticker}/metrics`).then(r => r.data)

// ── Calibration ───────────────────────────────────────────────────────────────
export const fetchCalibrationReport = (): Promise<CalibrationReport> =>
  api.get('/calibration/report').then(r => r.data)

// ── Batch Analysis ────────────────────────────────────────────────────────────
export const submitBatch = (tickers: string[], name?: string): Promise<BatchJob> =>
  api.post('/batch', { tickers, name }).then(r => r.data)

export const fetchBatch = (batchId: string): Promise<BatchJob> =>
  api.get(`/batch/${batchId}`).then(r => r.data)

export const fetchBatchResults = (batchId: string): Promise<BatchResultsResponse> =>
  api.get(`/batch/${batchId}/results`).then(r => r.data)

export const fetchBatches = (): Promise<BatchListResponse> =>
  api.get('/batch').then(r => r.data)

// ── Factor Analytics ──────────────────────────────────────────────────────────
// NOTE: callers narrow to their own local type -- FactorUniverseResponse describes the shape
export const cacheKeyFactorUniverse = (params?: { sector?: string; limit?: number; sort_by?: string }): string =>
  `GET /factors/universe?${new URLSearchParams(
    Object.entries(params ?? {}).filter(([, v]) => v != null).map(([k, v]) => [k, String(v)]),
  ).toString()}`
export const fetchFactorUniverse = (params?: { sector?: string; limit?: number; sort_by?: string }): Promise<FactorUniverseResponse & { tickers?: unknown[] }> =>
  cachedFetch(cacheKeyFactorUniverse(params), () => api.get('/factors/universe', { params }).then(r => r.data))

export const fetchTickerFactors = (ticker: string): Promise<TickerFactorResponse> =>
  api.get(`/factors/${ticker}`).then(r => r.data)

export const screenByFactors = (filters: {
  min_value?: number
  min_growth?: number
  min_quality?: number
  min_momentum?: number
  min_composite?: number
  sector?: string
  limit?: number
}): Promise<FactorScreenResponse> =>
  api.post('/factors/screen', filters).then(r => r.data)

// ── ML Prediction Engine ───────────────────────────────────────────────────────
export const trainMLModel = (): Promise<MLTrainResult> =>
  api.post('/ml/train').then(r => r.data)

export const seedMLData = (force = false): Promise<MLSeedResult> =>
  api.post(`/ml/seed${force ? '?force=true' : ''}`).then(r => r.data)

// NOTE: callers define their own local MLPrediction type -- return unknown to avoid clash
export const fetchMLPrediction = (ticker: string): Promise<unknown> =>
  api.get(`/ml/predict/${ticker}`).then(r => r.data)

// NOTE: callers define their own local ModelStats type -- return unknown to avoid clash
export const CACHE_KEY_ML_STATS = 'GET /ml/model/stats'
export const fetchMLStats = (): Promise<unknown> =>
  cachedFetch(CACHE_KEY_ML_STATS, () => api.get('/ml/model/stats').then(r => r.data))

// NOTE: callers access .factors or .feature_importance -- return unknown
export const CACHE_KEY_ML_FACTORS = 'GET /ml/factors'
export const fetchMLFactors = (): Promise<unknown> =>
  cachedFetch(CACHE_KEY_ML_FACTORS, () => api.get('/ml/factors').then(r => r.data))

export const fetchMLBatchPredict = (tickers: string[]): Promise<MLBatchPrediction[]> =>
  api.get('/ml/batch-predict', { params: { tickers: tickers.join(',') } }).then(r => r.data)

// ── Usage Metering ─────────────────────────────────────────────────────────────
export const fetchUsageSummary = (): Promise<UsageSummary> =>
  api.get('/usage/summary').then(r => r.data)

export const fetchUsageHistory = (params?: { limit?: number; offset?: number }): Promise<UsageHistoryResponse> =>
  api.get('/usage/history', { params }).then(r => r.data)

export const fetchUsageQuota = (): Promise<UsageQuota> =>
  api.get('/usage/quota').then(r => r.data)

// ── Portfolio Risk ─────────────────────────────────────────────────────────────
export const fetchPortfolioExposure = (portfolioId: string): Promise<PortfolioExposure> =>
  api.get(`/portfolio-risk/${portfolioId}/exposure`).then(r => r.data)

export const fetchPortfolioVaR = (portfolioId: string): Promise<PortfolioVaR> =>
  api.get(`/portfolio-risk/${portfolioId}/var`).then(r => r.data)

export const fetchPortfolioAttribution = (portfolioId: string): Promise<PortfolioAttribution> =>
  api.get(`/portfolio-risk/${portfolioId}/attribution`).then(r => r.data)

export const fetchPortfolioFrontier = (portfolioId: string): Promise<PortfolioFrontier> =>
  api.get(`/portfolio-risk/${portfolioId}/frontier`).then(r => r.data)

export const computeAdHocVaR = (tickers: string[], weights: number[]): Promise<AdHocVaRResult> =>
  api.post('/portfolio-risk/var', { tickers, weights }).then(r => r.data)

export const computeAdHocFrontier = (tickers: string[]): Promise<AdHocFrontierResult> =>
  api.post('/portfolio-risk/frontier', { tickers }).then(r => r.data)

// ── Systematic Strategies ─────────────────────────────────────────────────────
export const fetchStrategyTypes = (): Promise<StrategyTypeSchema[]> =>
  api.get('/strategies/types').then(r => r.data?.schemas ?? r.data)

export const runStrategy = (name: string, strategy_type: string, parameters: Record<string, unknown>): Promise<StrategyRun> =>
  api.post('/strategies/run', { name, strategy_type, parameters }).then(r => r.data?.result ?? r.data)

export const previewStrategy = (strategy_type: string, parameters: Record<string, unknown>): Promise<StrategyRun> =>
  api.post('/strategies/preview', { strategy_type, parameters }).then(r => r.data?.result ?? r.data)

// NOTE: API may return { runs: StrategyRun[], total: number } or StrategyRun[] -- callers handle both
export const CACHE_KEY_STRATEGIES = 'GET /strategies'
export const fetchStrategies = (): Promise<StrategyRun[] | { runs: StrategyRun[]; total?: number }> =>
  cachedFetch(CACHE_KEY_STRATEGIES, () => api.get('/strategies').then(r => r.data))

// Strategies.tsx calls api.get('/strategies/universe') directly. Expose the
// cached form so prefetch + page-mount share one round-trip.
export const CACHE_KEY_STRATEGIES_UNIVERSE = 'GET /strategies/universe'
export const fetchStrategiesUniverse = (): Promise<unknown> =>
  cachedFetch(CACHE_KEY_STRATEGIES_UNIVERSE, () => api.get('/strategies/universe').then(r => r.data))

export const fetchStrategyRun = (runId: string): Promise<StrategyRun> =>
  api.get(`/strategies/${runId}`).then(r => r.data)

// ── Portfolio Analytics Extensions (Tasks 4-6) ────────────────────────────────

export const fetchDrawdownAnalysis = (id: string, lookbackDays?: number): Promise<DrawdownAnalysis> =>
  api.get(`/portfolio/${id}/drawdown`, { params: lookbackDays != null ? { lookback_days: lookbackDays } : {} }).then(r => r.data)

export const fetchRollingMetrics = (id: string, lookbackDays?: number): Promise<RollingMetrics> =>
  api.get(`/portfolio/${id}/rolling`, { params: lookbackDays != null ? { lookback_days: lookbackDays } : {} }).then(r => r.data)

export const fetchConcentrationRisk = (id: string): Promise<ConcentrationRisk> =>
  api.get(`/portfolio-risk/${id}/concentration`).then(r => r.data)

// ── Portfolio Analytics Extensions (Tasks 7-10) ───────────────────────────────

// Task 7: What-If Rebalancer
export const runWhatIf = (
  id: string,
  proposedTickers: string[],
  proposedWeights: number[],
): Promise<WhatIfResult> =>
  api
    .post(`/portfolio/${id}/what-if`, {
      proposed_tickers: proposedTickers,
      proposed_weights: proposedWeights,
    })
    .then(r => r.data)

// Task 8: Correlation Heatmap
export const fetchCorrelationHeatmap = (
  id: string,
  lookbackDays = 252,
): Promise<{ labels: string[]; matrix: number[][] }> =>
  api
    .get(`/portfolio/${id}/correlation`, { params: { lookback_days: lookbackDays } })
    .then(r => r.data)

// Task 9: Portfolio Export - trigger browser downloads
export async function exportPortfolioCSV(id: string): Promise<void> {
  const resp = await api.get(`/portfolio/${id}/export/csv`, { responseType: 'blob' })
  const disposition = (resp.headers['content-disposition'] as string) ?? ''
  const match = disposition.match(/filename="?(.+?)"?$/i)
  const filename = match?.[1] ?? `portfolio_${id}.csv`
  const url = URL.createObjectURL(resp.data as Blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url) }, 200)
}

export async function exportPortfolioJSON(id: string, includeAnalytics = false): Promise<void> {
  const resp = await api.get(`/portfolio/${id}/export/json`, {
    params: { include_analytics: includeAnalytics },
    responseType: 'blob',
  })
  const disposition = (resp.headers['content-disposition'] as string) ?? ''
  const match = disposition.match(/filename="?(.+?)"?$/i)
  const filename = match?.[1] ?? `portfolio_${id}.json`
  const url = URL.createObjectURL(resp.data as Blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url) }, 200)
}

// Task 10: Risk Parity
export const fetchRiskParity = (id: string): Promise<RiskParityResult> =>
  api.get(`/portfolio-risk/${id}/risk-parity`).then(r => r.data)

export const computeAdHocRiskParity = (tickers: string[]): Promise<RiskParityResult> =>
  api.post('/portfolio-risk/risk-parity', { tickers }).then(r => r.data)

// ── Phase 5.2 Sub-Resource Endpoints ─────────────────────────────────
import type {
  QuantitativeResponse, CreditResponse, InsiderResponse,
  InstitutionalResponse, GovernanceResponse, EventsResponse,
  OptionsMarketResponse, MacroOverlayResponse, AttentionResponse,
  FactorModelResponse, RegimeBacktestResponse, CapitalAllocationResponse,
} from '@/types/phase52'

export const fetchQuantitative = (ticker: string): Promise<QuantitativeResponse> =>
  api.get(`/analysis/${ticker}/quantitative`).then(r => r.data)
export const fetchCredit = (ticker: string): Promise<CreditResponse> =>
  api.get(`/analysis/${ticker}/credit`).then(r => r.data)
export const fetchInsider = (ticker: string): Promise<InsiderResponse> =>
  api.get(`/analysis/${ticker}/insider`).then(r => r.data)
export const fetchInstitutional = (ticker: string): Promise<InstitutionalResponse> =>
  api.get(`/analysis/${ticker}/institutional`).then(r => r.data)
export const fetchGovernance = (ticker: string): Promise<GovernanceResponse> =>
  api.get(`/analysis/${ticker}/governance`).then(r => r.data)
export const fetchEventCalendar = (ticker: string): Promise<EventsResponse> =>
  api.get(`/analysis/${ticker}/events`).then(r => r.data)
export const fetchOptionsMarket = (ticker: string): Promise<OptionsMarketResponse> =>
  api.get(`/analysis/${ticker}/options-market`).then(r => r.data)
export const fetchMacroOverlay = (ticker: string): Promise<MacroOverlayResponse> =>
  api.get(`/analysis/${ticker}/macro-overlay`).then(r => r.data)
export const fetchAttention = (ticker: string): Promise<AttentionResponse> =>
  api.get(`/analysis/${ticker}/attention`).then(r => r.data)
export const fetchFactorModel = (ticker: string): Promise<FactorModelResponse> =>
  api.get(`/analysis/${ticker}/factor-model`).then(r => r.data)
export const fetchRegimeBacktest = (ticker: string): Promise<RegimeBacktestResponse> =>
  api.get(`/analysis/${ticker}/regime-backtest`).then(r => r.data)
export const fetchCapitalAllocation = (ticker: string): Promise<CapitalAllocationResponse> =>
  api.get(`/analysis/${ticker}/capital-allocation`).then(r => r.data)

// ── Enrichment sub-resources (Phase 5.3 high-value surfaces) ──────────────
import type { EnrichmentResponse } from '@/types/phase52'
export const fetchOptionsEnrichment = (ticker: string): Promise<EnrichmentResponse> =>
  api.get(`/analysis/${ticker}/options-chain`).then(r => r.data)
export const fetchClinicalPipeline = (ticker: string): Promise<EnrichmentResponse> =>
  api.get(`/analysis/${ticker}/clinical-pipeline`).then(r => r.data)
export const fetchRecommendationMomentum = (ticker: string): Promise<EnrichmentResponse> =>
  api.get(`/analysis/${ticker}/recommendation-momentum`).then(r => r.data)
export const fetchStreetTargets = (ticker: string): Promise<EnrichmentResponse> =>
  api.get(`/analysis/${ticker}/street-targets`).then(r => r.data)
export const fetchSupplyChain = (ticker: string): Promise<EnrichmentResponse> =>
  api.get(`/analysis/${ticker}/supply-chain`).then(r => r.data)
export const fetchPatentMetrics = (ticker: string): Promise<EnrichmentResponse> =>
  api.get(`/analysis/${ticker}/patent-metrics`).then(r => r.data)
export const fetchInsiderForm4 = (ticker: string): Promise<EnrichmentResponse> =>
  api.get(`/analysis/${ticker}/insider-form4`).then(r => r.data)
export const fetchExecutives = (ticker: string): Promise<EnrichmentResponse> =>
  api.get(`/analysis/${ticker}/executives`).then(r => r.data)
export const fetchEnergyContext = (ticker: string): Promise<EnrichmentResponse> =>
  api.get(`/analysis/${ticker}/energy-context`).then(r => r.data)
export const fetchAgricultureContext = (ticker: string): Promise<EnrichmentResponse> =>
  api.get(`/analysis/${ticker}/agriculture-context`).then(r => r.data)
export const fetchClimateContext = (ticker: string): Promise<EnrichmentResponse> =>
  api.get(`/analysis/${ticker}/climate-context`).then(r => r.data)
export const fetchDeveloperActivity = (ticker: string): Promise<EnrichmentResponse> =>
  api.get(`/analysis/${ticker}/developer-activity`).then(r => r.data)
export const fetchMacroIndicators = (ticker: string): Promise<EnrichmentResponse> =>
  api.get(`/analysis/${ticker}/macro-indicators`).then(r => r.data)


// ═════════════════════════════════════════════════════════════════════════════
// MOAT ANALYSIS (Phase 5.3 integration)
// ═════════════════════════════════════════════════════════════════════════════

export interface MoatJob {
  id: string
  job_id?: string
  ticker: string
  status: string
  phase?: string
  progress?: number
  created_at: string
  completed_at?: string | null
  error?: string | null
}

export const createMoatJob = (ticker: string) =>
  api.post<MoatJob>('/moat/jobs', { ticker }).then(r => r.data)

export const listMoatJobs = (params?: { status?: string; limit?: number; offset?: number; all?: boolean }) =>
  api.get<{ jobs: MoatJob[]; total: number; limit: number; offset: number }>('/moat/jobs', { params })
    .then(r => r.data?.jobs ?? [])

export const getMoatJob = (jobId: string) =>
  api.get<MoatJob>(`/moat/jobs/${jobId}`).then(r => r.data)

export const deleteMoatJob = (jobId: string) =>
  api.delete(`/moat/jobs/${jobId}`)

// NOTE: callers may cast to their own MoatAnalysis (lib/moat-types.ts) -- return unknown for flexibility
export const getMoatAnalysis = (ticker: string): Promise<unknown> =>
  api.get(`/moat/analysis/${ticker}`).then(r => r.data)

export const getMoatSummary = (ticker: string): Promise<MoatSummaryResponse> =>
  api.get(`/moat/analysis/${ticker}/summary`).then(r => r.data)

// ═════════════════════════════════════════════════════════════════════════════
// CALIBRATION ENGINE
// ═════════════════════════════════════════════════════════════════════════════

export interface CalibrationAdjustment {
  id?: string
  sector?: string | null
  wacc_bias: number
  growth_dampening: number
  multiple_compression: number
  confidence: number
  sample_size: number
  mae: number
  hit_rate: number
  computed_at?: string
}

export interface CalibrationReport {
  global_calibration: CalibrationAdjustment | null
  sector_calibrations: CalibrationAdjustment[]
  total_backtesting_records: number
  coverage_tickers: number
  last_computed: string | null
}

export const fetchSectorCalibration = (sector: string) =>
  api.get<CalibrationAdjustment>(`/calibration/sector/${encodeURIComponent(sector)}`).then(r => r.data)

export const recomputeCalibrations = () =>
  api.post<{ status: string; global_sample_size: number; sectors_computed: number; message: string }>(
    '/calibration/recompute'
  ).then(r => r.data)

export const fetchCalibrationHistory = (limit = 50) =>
  api.get<CalibrationAdjustment[]>('/calibration/history', { params: { limit } }).then(r => r.data)

export interface CalibrationApplyResponse {
  ticker: string
  calibration_applied: boolean
  sector?: string | null
  wacc_original?: number | null
  wacc_adjusted?: number | null
  growth_original?: number | null
  growth_adjusted?: number | null
  confidence?: number | null
  sample_size?: number | null
  reason: string
  adjusted_analysis?: Record<string, unknown> | null
}

export const applyCalibration = (ticker: string) =>
  api.post<CalibrationApplyResponse>(`/calibration/apply/${ticker}`).then(r => r.data)

// ═════════════════════════════════════════════════════════════════════════════
// INSTITUTIONAL OWNERSHIP -- additional endpoints (flows, history, crowded)
// ═════════════════════════════════════════════════════════════════════════════

export interface OwnershipFlow {
  ticker: string
  period: string
  net_shares_change: number
  net_value_change_usd: number
  new_positions: number
  closed_positions: number
  increased_positions: number
  decreased_positions: number
}

export interface OwnershipHistorySnapshot {
  report_date: string
  total_holders?: number
  institutional_pct?: number
  top10_concentration?: number
}

export interface OwnershipHistory {
  ticker: string
  snapshots: OwnershipHistorySnapshot[]
}

export interface CrowdedTrade {
  ticker: string
  crowding_score: number
  institutional_ownership_pct: number
  top_10_concentration: number
  smart_money_signal: string
  report_date?: string
}

export const fetchOwnershipFlows = (ticker: string) =>
  api.get<OwnershipFlow>(`/institutional/${ticker}/flows`).then(r => r.data)

export const fetchOwnershipHistory = (ticker: string) =>
  api.get<OwnershipHistory>(`/institutional/${ticker}/history`).then(r => r.data)

export const fetchCrowdedTrades = (limit = 25) =>
  api.get<CrowdedTrade[]>('/institutional/crowded', { params: { limit } }).then(r => r.data)

// ═════════════════════════════════════════════════════════════════════════════
// CREDIT -- universe endpoints (screen + dashboard)
// ═════════════════════════════════════════════════════════════════════════════

export interface CreditScreenRow {
  ticker: string
  credit_rating?: string | null
  implied_spread_bps?: number | null
  leverage_ratio?: number | null
  interest_coverage?: number | null
  default_probability_1yr?: number | null
  relative_value_signal?: string | null
  error?: string | null
}

export interface CreditDashboard {
  coverage_count: number
  investment_grade_count: number
  high_yield_count: number
  distressed_count: number
  avg_leverage: number
  avg_interest_coverage: number
  signal_distribution: Record<string, number>
  heat_map: CreditScreenRow[]
}

export const screenCredit = (tickers: string[]) =>
  api.post<CreditScreenRow[]>('/credit/screen', { tickers }).then(r => r.data)

export const fetchCreditDashboard = () =>
  api.get<CreditDashboard>('/credit/dashboard').then(r => r.data)

// ═════════════════════════════════════════════════════════════════════════════
// ESTIMATES -- refresh actuals
// ═════════════════════════════════════════════════════════════════════════════

export const refreshEstimateActuals = (ticker: string) =>
  api.post<{ ticker: string; actuals_upserted: number }>(`/estimates/${ticker}/refresh-actuals`).then(r => r.data)

// ═════════════════════════════════════════════════════════════════════════════
// WATCHLIST -- alert update (PATCH)
// ═════════════════════════════════════════════════════════════════════════════

export interface AlertUpdatePayload {
  threshold_value?: number
  direction?: string
  enabled?: boolean
}

export const updateAlert = (alertId: number, updates: AlertUpdatePayload) =>
  api.patch(`/watchlist/alerts/${alertId}`, updates).then(r => r.data)

// ═════════════════════════════════════════════════════════════════════════════
// CONTRARIAN SIGNALS
// ═════════════════════════════════════════════════════════════════════════════

export interface ContrarySignal {
  ticker: string
  company_name: string
  short_interest_pct: number
  short_interest_delta_6m: number
  eps_revision_score: number
  revenue_acceleration: number
  margin_inflection: number
  composite_score: number
  catalyst_date: string | null
  evidence_snippet: string
  short_data_source: string
  scored_at: string
}

export interface ScreenResponse {
  signals: ContrarySignal[]
  universe_size: number
  generated_at: string
}

export const screenContrarian = (params: {
  universe?: string[]
  min_short_interest?: number
  min_composite_score?: number
  limit?: number
}) => api.post<ScreenResponse>('/contrarian/screen', params).then(r => r.data)

export const fetchContrarianSignal = (ticker: string) =>
  api.get<ContrarySignal>(`/contrarian/signal/${ticker}`).then(r => r.data)

// ═════════════════════════════════════════════════════════════════════════════
// PEER GROUP COMP TABLE
// ═════════════════════════════════════════════════════════════════════════════

export const fetchPeerGroupCompTable = (id: string) =>
  api.get<{ group_id: string; group_name: string; rows: Record<string, unknown>[] }>(
    `/peer-groups/${id}/comp-table`
  ).then(r => r.data)

// ═════════════════════════════════════════════════════════════════════════════
// THEMATIC RESEARCH
// ═════════════════════════════════════════════════════════════════════════════

export interface ThematicResult {
  ticker: string
  company_name: string
  relevance_score: number
  evidence: string
}

export const searchThematic = (params: {
  theme?: string
  keywords?: string[]
  limit?: number
}) => api.post<{ results: ThematicResult[]; theme: string; generated_at: string }>(
  '/ouroboros/thematic', params
).then(r => r.data)

// ═════════════════════════════════════════════════════════════════════════════
// PODCAST INTELLIGENCE
// ═════════════════════════════════════════════════════════════════════════════

export interface PodcastFeed {
  id: string
  title: string
  url: string
  category: string
  episode_count: number
}

export interface PodcastEpisode {
  id: string
  title: string
  published: string
  duration_seconds: number | null
  summary: string
}

export const fetchPodcastFeeds = (): Promise<PodcastFeed[]> =>
  api.get<{ feeds: string[] }>('/podcast/feeds').then(r =>
    r.data.feeds.map(name => ({
      id: name,
      title: name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      url: '',
      category: 'Finance',
      episode_count: 0,
    }))
  )

export const fetchPodcastEpisodes = (feedName: string): Promise<PodcastEpisode[]> =>
  api.get<{ feed: string; episode_count: number; episodes: PodcastEpisode[] }>(
    `/podcast/episodes/${feedName}`, { params: { limit: 10 } }
  ).then(r => r.data.episodes)

export const fetchPodcastTranscript = (url: string) =>
  api.post<{ transcript: string; segments: Array<{ start: number; text: string }> }>(
    '/podcast/transcript', { url }
  ).then(r => r.data)

// ═════════════════════════════════════════════════════════════════════════════
// SECTOR SKILLS (KPIs)
// ═════════════════════════════════════════════════════════════════════════════

export interface SectorKPI {
  ticker: string
  sector: string
  kpis: Record<string, number | string | null>
}

export const fetchSectorList = () =>
  api.get<{ sectors: string[] }>('/sector-skills/sectors').then(r => r.data.sectors)

export const fetchSectorKPIs = (ticker: string, sector_hint?: string) =>
  api.get<SectorKPI>(`/sector-skills/${ticker}`, {
    params: sector_hint ? { sector_hint } : undefined,
  }).then(r => r.data)

export const fetchSectorKPIsBatch = (tickers: string[]) =>
  api.post<SectorKPI[]>('/sector-skills/batch', { tickers }).then(r => r.data)

// ═════════════════════════════════════════════════════════════════════════════
// RESEARCH INBOX
// ═════════════════════════════════════════════════════════════════════════════

export interface InboxItem {
  id: string
  from_addr: string
  subject: string
  tickers: string[]
  received_at: string | null
  ingested_at: string
  evidence_ids: string[]
}

export const fetchResearchInbox = (params?: { limit?: number; ticker?: string }) =>
  api.get<{ count: number; items: InboxItem[] }>('/inbox', { params }).then(r => r.data.items)

export const fetchInboxItem = (id: string) =>
  api.get<InboxItem>(`/inbox/${id}`).then(r => r.data)

// ═════════════════════════════════════════════════════════════════════════════
// DATA PIPELINES
// ═════════════════════════════════════════════════════════════════════════════

export interface Pipeline {
  name: string
  description: string
  url_template: string
  field_selectors: Record<string, string>
}

export const fetchPipelines = () =>
  api.get<{ pipelines: Pipeline[]; allowed_domains: string[] }>('/pipelines').then(r => r.data)

export const runPipeline = (pipelineName: string, params?: Record<string, unknown>) =>
  api.post<Record<string, unknown>>('/pipelines/run', {
    pipeline_name: pipelineName, params: params ?? {},
  }).then(r => r.data)

export const customFetch = (url: string, fieldSelectors?: Record<string, string>) =>
  api.post<Record<string, unknown>>(
    '/pipelines/fetch', { url, field_selectors: fieldSelectors ?? {} }
  ).then(r => r.data)
